// Maps a Live View report onto the builder graph: per-node metric objects (for
// the on-canvas chips + drill-down) and per-edge counts. Correlation uses the
// report's node_steps (nodeId -> step number) and send_by_node (nodeId -> funnel).
//
// Honesty rules baked in here:
//  - `tAvail` (transitions available) is threaded onto every metric so the UI
//    shows "—" (unavailable) rather than a fake 0 for entered/waiting when no
//    transition data exists yet.
//  - message-funnel counts come from a separate source (send_by_node) and are
//    shown independently of transition availability.

export function buildOverlay(report, nodes, edges) {
  const metrics = {};
  const edgeLabels = {};
  if (!report) return { metrics, edgeLabels, available: {} };

  const { node_steps = {}, by_step = {}, send_by_node = {}, totals = {}, entry_node_id, available = {} } = report;
  const tAvail = !!available.transitions;
  const byId = {};
  nodes.forEach((n) => { byId[n.id] = n; });

  nodes.forEach((n) => {
    const isEntry = n.id === entry_node_id || n.type === 'entry' || n.type.startsWith('entry-');
    if (isEntry) {
      metrics[n.id] = { kind: 'entry', tAvail, admitted: totals.admitted || 0, active: totals.active || 0, completed: totals.completed || 0 };
      return;
    }
    const step = node_steps[n.id];
    const sd = step != null ? by_step[step] : null;
    if ((n.type || '').startsWith('action-')) {
      const f = send_by_node[n.id] || {};
      metrics[n.id] = {
        kind: 'send', tAvail,
        entered: sd ? sd.entered : null,
        waiting: sd ? sd.waiting_now : null,
        sent: f.sent || 0, delivered: f.delivered || 0, opened: f.opened || 0, clicked: f.clicked || 0, failed: f.failed || 0,
        hasSend: !!send_by_node[n.id],
      };
    } else if (n.type === 'logic-condition' || n.type === 'logic-ab') {
      metrics[n.id] = { kind: 'branch', tAvail, entered: sd ? sd.entered : null, branches: sd ? sd.branches : {} };
    } else if (n.type === 'logic-goal' || n.type === 'wait-relative' || n.type === 'wait-absolute') {
      metrics[n.id] = { kind: 'step', tAvail, entered: sd ? sd.entered : null, waiting: sd ? sd.waiting_now : null };
    } else if (n.type === 'logic-exit') {
      metrics[n.id] = { kind: 'exit', tAvail, completed: totals.completed || 0 };
    }
  });

  edges.forEach((e) => {
    if (!tAvail) return;
    const src = byId[e.from];
    if (!src) return;
    let val;
    if (src.type === 'logic-condition' || src.type === 'logic-ab') {
      const s = node_steps[src.id];
      const b = s != null ? by_step[s]?.branches : null;
      val = b ? b[e.fromHandle] : undefined;
    } else {
      const ts = node_steps[e.to];
      val = ts != null ? by_step[ts]?.entered : undefined;
    }
    if (val !== undefined) edgeLabels[e.id] = val;
  });

  return { metrics, edgeLabels, available };
}
